"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { GraphErrorBoundary } from "@/components/aicheck/GraphErrorBoundary";
import { apiUrl } from "@/lib/aicheck/api-base";
import type { RunRecord } from "@/lib/aicheck/history/types";
import { authService } from "@/services/auth";
import type {
  AdminNodeAction,
  NodeExecutionDirective,
  OutputLanguage,
  RunProgress as RunProgressType,
} from "@/types/aicheck";
import { PipelineGraph } from "./pipeline-graph/PipelineGraph";

interface RunProgressGraphProps {
  progress: RunProgressType;
  projectId?: number;
  /** U2: 過去実績（同一言語・完了・非freeform）から所要時間の目安を出すためのフィルタキー */
  language?: OutputLanguage;
  onAbort?: () => void;
  onCancel?: () => void;
  onRetry?: () => void;
  onRetryFailed?: () => void;
  onAdminAction?: (action: AdminNodeAction, nodeId: string) => void;
  onResume?: () => void;
  onDismiss?: () => void;
}

const GRAPH_EXPANDED_KEY = "aicheck:graph-expanded:";

function readExpanded(pid: number | undefined): boolean {
  if (!pid) return false;
  try {
    return localStorage.getItem(`${GRAPH_EXPANDED_KEY}${pid}`) === "1";
  } catch {
    return false;
  }
}

function writeExpanded(pid: number | undefined, expanded: boolean) {
  if (!pid) return;
  try {
    if (expanded) localStorage.setItem(`${GRAPH_EXPANDED_KEY}${pid}`, "1");
    else localStorage.removeItem(`${GRAPH_EXPANDED_KEY}${pid}`);
  } catch {
    /* ignore */
  }
}

function formatNodeExecutionMessage(
  directives: NodeExecutionDirective[] | undefined,
): string | null {
  const scheduledCount =
    directives?.filter((directive) => directive.state === "scheduled").length ??
    0;
  const staleCount =
    directives?.filter((directive) => directive.state === "stale").length ?? 0;
  if (scheduledCount > 0) {
    return `${scheduledCount} subtree abort reservation${scheduledCount > 1 ? "s" : ""} active`;
  }
  if (staleCount > 0) {
    return "Abort reservations were restored as stale markers after server state was lost.";
  }
  return null;
}

export function RunProgressGraph({
  progress,
  projectId,
  language,
  onAbort,
  onCancel,
  onRetry,
  onRetryFailed,
  onAdminAction,
  onResume,
  onDismiss,
}: RunProgressGraphProps) {
  const isFromCache = progress.currentStep === "Loaded from cache";
  const adminControlMessage = formatNodeExecutionMessage(
    progress.nodeExecutionDirectives,
  );
  const [expanded, setExpanded] = useState(() =>
    isFromCache ? false : readExpanded(projectId),
  );

  // Sync expanded state when projectId changes or cache loads
  useEffect(() => {
    setExpanded(isFromCache ? false : readExpanded(projectId));
  }, [projectId, isFromCache]);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      writeExpanded(projectId, next);
      return next;
    });
  }, [projectId]);

  const [activeCollapsed, setActiveCollapsed] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  // Track previous status to detect active → terminal transition
  const prevStatusRef = useRef(progress.status);
  const [collapseAnimating, setCollapseAnimating] = useState(false);

  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = progress.status;

    const wasActive =
      prev !== "idle" &&
      prev !== "completed" &&
      prev !== "aborted" &&
      prev !== "cancelled" &&
      prev !== "error" &&
      prev !== "disconnected";
    const isNowTerminal =
      progress.status === "completed" ||
      progress.status === "aborted" ||
      progress.status === "cancelled" ||
      progress.status === "error" ||
      progress.status === "disconnected";

    if (wasActive && isNowTerminal && !isFromCache) {
      // Keep error state visible so retry choices remain immediately accessible.
      if (progress.status === "error") {
        setExpanded(true);
        setActiveCollapsed(false);
        setCollapseAnimating(false);
        writeExpanded(projectId, true);
        return;
      }
      // Show graph briefly, then animate collapse
      setExpanded(true);
      setActiveCollapsed(false);
      setCollapseAnimating(false);
      const timer = setTimeout(() => {
        setCollapseAnimating(true);
        // After animation finishes, set expanded=false
        const cleanup = setTimeout(() => {
          setExpanded(false);
          setCollapseAnimating(false);
          writeExpanded(projectId, false);
        }, 500);
        return () => clearTimeout(cleanup);
      }, 1500);
      return () => clearTimeout(timer);
    }

    if (isNowTerminal) {
      setActiveCollapsed(false);
    }
  }, [progress.status, isFromCache, projectId]);

  if (progress.status === "idle") return null;

  const isTerminal =
    progress.status === "completed" ||
    progress.status === "aborted" ||
    progress.status === "cancelled" ||
    progress.status === "error" ||
    progress.status === "disconnected";

  const hasFailures =
    !!progress.failedStage || (progress.failedChecks?.length ?? 0) > 0;
  const hasErrorDetails =
    !!progress.error ||
    !!progress.stageError ||
    (progress.failedChecks?.length ?? 0) > 0;

  // --- Terminal states: summary bar + collapsible DAG ---
  if (isTerminal) {
    const { color, label } = getSummaryStyle(progress, hasFailures);

    return (
      <div
        className={`rounded-lg border ${color.border} ${color.bg} overflow-hidden`}
      >
        {/* Summary bar — clickable toggle */}
        {/* biome-ignore lint/a11y/useSemanticElements: div avoids nested-button hydration error */}
        <div
          role="button"
          tabIndex={0}
          onClick={toggleExpanded}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleExpanded();
            }
          }}
          className={`w-full flex items-center justify-between px-4 py-3 cursor-pointer ${color.hoverBg} transition-colors`}
        >
          <div className="flex items-center gap-2 text-sm font-medium">
            <div className="flex flex-col">
              <span className={color.text}>{label}</span>
              {adminControlMessage && (
                <span className="text-[11px] font-normal text-gray-500">
                  {adminControlMessage}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Action buttons */}
            {progress.status === "completed" &&
              hasFailures &&
              hasErrorDetails && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowErrorDialog(true);
                  }}
                  className={`rounded border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${color.border} ${color.bg} ${color.text} hover:opacity-90`}
                >
                  Error details
                </button>
              )}
            {progress.status === "completed" &&
              hasFailures &&
              onRetryFailed && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRetryFailed();
                  }}
                  className={`rounded border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${color.border} ${color.bg} ${color.text} hover:opacity-90`}
                >
                  Retry from failed step
                </button>
              )}
            {progress.status === "completed" && hasFailures && onRetry && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry();
                }}
                className={`rounded border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${color.border} ${color.bg} ${color.text} hover:opacity-90`}
              >
                Rerun full check from start
              </button>
            )}
            {progress.status === "aborted" && onResume && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onResume();
                }}
                className={`rounded border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${color.border} ${color.bg} ${color.text} hover:opacity-90`}
              >
                Resume
              </button>
            )}
            {progress.status === "aborted" && onRetry && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry();
                }}
                className={`rounded border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${color.border} ${color.bg} ${color.text} hover:opacity-90`}
              >
                Rerun full check from start
              </button>
            )}
            {progress.status === "cancelled" && onRetry && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry();
                }}
                className={`rounded border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${color.border} ${color.bg} ${color.text} hover:opacity-90`}
              >
                Run again
              </button>
            )}
            {progress.status === "error" && hasErrorDetails && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowErrorDialog(true);
                }}
                className={`rounded border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${color.border} ${color.bg} ${color.text} hover:opacity-90`}
              >
                Error details
              </button>
            )}
            {progress.status === "error" && hasFailures && onRetryFailed && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRetryFailed();
                }}
                className={`rounded border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${color.border} ${color.bg} ${color.text} hover:opacity-90`}
              >
                Retry from failed step
              </button>
            )}
            {progress.status === "error" && onRetry && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry();
                }}
                className={`rounded border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${color.border} ${color.bg} ${color.text} hover:opacity-90`}
              >
                Rerun full check from start
              </button>
            )}
            {progress.status === "disconnected" && onResume && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onResume();
                }}
                className={`rounded border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${color.border} ${color.bg} ${color.text} hover:opacity-90`}
              >
                Resume
              </button>
            )}
            {/* Chevron */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className={`${color.text} transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
              aria-hidden="true"
            >
              <path
                d="M4 6l4 4 4-4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* Collapsible DAG with animation */}
        {expanded && (
          <div
            className="border-t border-gray-200 bg-white grid transition-[grid-template-rows] duration-500 ease-out"
            style={{
              gridTemplateRows: collapseAnimating ? "0fr" : "1fr",
            }}
          >
            <div className="overflow-hidden">
              <GraphErrorBoundary>
                <PipelineGraph
                  progress={progress}
                  onCancel={onCancel}
                  onRetry={onRetry}
                  onRetryFailed={onRetryFailed}
                  onAdminAction={onAdminAction}
                  onDismiss={onDismiss}
                  onCollapse={toggleExpanded}
                />
              </GraphErrorBoundary>
            </div>
          </div>
        )}
        {showErrorDialog && (
          <ErrorDetailsDialog
            progress={progress}
            onClose={() => setShowErrorDialog(false)}
          />
        )}
      </div>
    );
  }

  // --- Active: show pipeline graph ---
  return (
    <div className="rounded-lg border border-blue-200 bg-white shadow-sm overflow-hidden">
      {/* Header — always clickable to toggle graph */}
      {/* biome-ignore lint/a11y/useSemanticElements: div avoids nested-button hydration error */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setActiveCollapsed((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setActiveCollapsed((v) => !v);
          }
        }}
        className="w-full flex items-center justify-between px-3 py-2 border-b border-blue-100 cursor-pointer hover:bg-blue-50/30 transition-colors"
      >
        <div className="flex items-center gap-2 text-sm">
          <div className="flex flex-col">
            <span className="font-medium text-gray-800">
              {progress.currentStep || "Running..."}
            </span>
            {adminControlMessage && (
              <span className="text-[11px] text-gray-500">
                {adminControlMessage}
              </span>
            )}
          </div>
          {progress.totalChecks > 0 && (
            <span className="text-gray-500 tabular-nums">
              {progress.completedChecks}/{progress.totalChecks}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {progress.startedAt && (
            <span className="flex items-center gap-1.5">
              <ElapsedTimer startedAt={progress.startedAt} />
              <EstimatedDurationHint
                projectId={projectId}
                language={language}
                startedAt={progress.startedAt}
              />
            </span>
          )}
          {onAbort && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAbort();
              }}
              className="rounded border border-gray-300 px-2.5 py-0.5 text-xs font-medium text-gray-500 transition-colors hover:border-orange-300 hover:text-orange-600"
            >
              Abort
            </button>
          )}
          {onCancel && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
              className="rounded border border-gray-300 px-2.5 py-0.5 text-xs font-medium text-gray-500 transition-colors hover:border-red-300 hover:text-red-600"
            >
              Cancel
            </button>
          )}
          {/* Chevron */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            className={`text-blue-500 transition-transform duration-200 ${activeCollapsed ? "" : "rotate-180"}`}
            aria-hidden="true"
          >
            <path
              d="M4 6l4 4 4-4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
      {/* Pipeline DAG */}
      {!activeCollapsed && (
        <GraphErrorBoundary>
          <PipelineGraph
            progress={progress}
            onCancel={onCancel}
            onRetry={onRetry}
            onRetryFailed={onRetryFailed}
            onAdminAction={onAdminAction}
            onDismiss={onDismiss}
            onCollapse={() => setActiveCollapsed(true)}
          />
        </GraphErrorBoundary>
      )}
    </div>
  );
}

function ErrorDetailsDialog({
  progress,
  onClose,
}: {
  progress: RunProgressType;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="nodrag bg-white rounded-lg shadow-xl border border-red-200 w-[min(640px,92vw)] max-h-[75vh] overflow-auto">
        <div className="px-4 py-2.5 border-b border-red-100 flex items-center justify-between">
          <div className="nodrag cursor-text select-text text-sm font-semibold text-red-700">
            Run Error Details
          </div>
          <button
            type="button"
            onClick={onClose}
            className="nodrag text-xs text-gray-500 hover:text-gray-700"
          >
            Close
          </button>
        </div>
        <div className="p-3 space-y-2">
          {progress.error && (
            <div className="rounded border border-red-200 bg-red-50 p-2">
              <div className="nodrag cursor-text select-text text-xs font-semibold text-red-700">
                Run error
              </div>
              <div className="nodrag cursor-text select-text mt-1 text-xs text-red-700 whitespace-pre-wrap break-words">
                {progress.error}
              </div>
            </div>
          )}
          {progress.stageError && (
            <div className="rounded border border-red-200 bg-red-50 p-2">
              <div className="nodrag cursor-text select-text text-xs font-semibold text-red-700">
                Failed stage ({progress.failedStage ?? "unknown"})
              </div>
              <div className="nodrag cursor-text select-text mt-1 text-xs text-red-700 whitespace-pre-wrap break-words">
                {progress.stageError}
              </div>
            </div>
          )}
          {(progress.failedChecks?.length ?? 0) > 0 && (
            <div className="space-y-2">
              <div className="nodrag cursor-text select-text text-xs font-semibold text-red-700">
                Failed checks
              </div>
              {progress.failedChecks?.map((check) => (
                <div
                  key={`${check.checkId}-${check.error}`}
                  className="rounded border border-red-200 bg-red-50 p-2"
                >
                  <div className="nodrag cursor-text select-text text-xs font-medium text-red-700">
                    {check.checkName}{" "}
                    <span className="text-[11px] text-red-500">
                      ({check.checkId})
                    </span>
                  </div>
                  {check.batchGroupName && (
                    <div className="nodrag cursor-text select-text mt-1 text-[11px] text-red-600">
                      Batch: {check.batchGroupName}
                    </div>
                  )}
                  <div className="nodrag cursor-text select-text mt-1 text-xs text-red-700 whitespace-pre-wrap break-words">
                    {check.error}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getSummaryStyle(
  progress: RunProgressType,
  hasFailures: boolean,
): {
  color: {
    border: string;
    bg: string;
    hoverBg: string;
    text: string;
  };
  label: string;
} {
  if (progress.status === "error") {
    return {
      color: {
        border: "border-red-200",
        bg: "bg-red-50",
        hoverBg: "hover:bg-red-100/50",
        text: "text-red-700",
      },
      label: "Run failed",
    };
  }

  if (progress.status === "disconnected") {
    return {
      color: {
        border: "border-orange-200",
        bg: "bg-orange-50",
        hoverBg: "hover:bg-orange-100/50",
        text: "text-orange-700",
      },
      label: progress.lastRecoveryError
        ? `Connection lost — ${progress.lastRecoveryError}`
        : "Connection lost. Resume available.",
    };
  }

  if (progress.status === "aborted") {
    return {
      color: {
        border: "border-orange-200",
        bg: "bg-orange-50",
        hoverBg: "hover:bg-orange-100/50",
        text: "text-orange-700",
      },
      label: "Check aborted. Resume available.",
    };
  }

  if (progress.status === "cancelled") {
    return {
      color: {
        border: "border-amber-200",
        bg: "bg-amber-50",
        hoverBg: "hover:bg-amber-100/50",
        text: "text-amber-700",
      },
      label: "Check was cancelled",
    };
  }

  // completed
  if (hasFailures) {
    return {
      color: {
        border: "border-amber-200",
        bg: "bg-amber-50",
        hoverBg: "hover:bg-amber-100/50",
        text: "text-amber-700",
      },
      label: "Check halted",
    };
  }

  return {
    color: {
      border: "border-green-200",
      bg: "bg-green-50",
      hoverBg: "hover:bg-green-100/50",
      text: "text-green-700",
    },
    label: "Check complete",
  };
}

function ElapsedTimer({ startedAt }: { startedAt: number }) {
  const [elapsed, setElapsed] = useState("");

  useEffect(() => {
    const update = () => {
      const totalSeconds = Math.floor((Date.now() - startedAt) / 1000);
      if (totalSeconds < 60) {
        setElapsed(`${totalSeconds}s`);
      } else {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        setElapsed(`${minutes}m ${seconds}s`);
      }
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  return <span className="text-gray-400 tabular-nums text-xs">{elapsed}</span>;
}

interface EstimatedDurationState {
  /** 過去実績から算出した中央値（分）。履歴なし/取得失敗/未ロードなら null */
  medianMinutes: number | null;
  /** exceeded 判定用の中央値（秒）。null の間は静的フォールバックの上限（5分）を使う */
  medianSeconds: number | null;
}

/**
 * U2: 同一案件・同一言語の過去実績（完了・非freeform run）から所要時間の中央値を算出する。
 * - フィルタ条件は RunDiffSummaryBadges の diff チェーン算出条件と揃える
 *   （status === "completed" かつ trigger !== "freeform"）。
 *   freeform（選択チェックのみの再実行等）は所要時間の性質が異なるため母集団から除外する。
 * - language が渡された場合はさらに同一言語に絞る（翻訳有無で所要時間が変わり得るため）。
 * - projectId 変化時のみ再フェッチ。progress の更新（頻繁）では再フェッチしない。
 */
function useEstimatedRunDuration(
  projectId: number | undefined,
  language: OutputLanguage | undefined,
): EstimatedDurationState {
  const [state, setState] = useState<EstimatedDurationState>({
    medianMinutes: null,
    medianSeconds: null,
  });

  useEffect(() => {
    setState({ medianMinutes: null, medianSeconds: null });
    if (!projectId || projectId <= 0) return;
    let cancelled = false;

    void (async () => {
      try {
        const token = await authService.getIdToken();
        if (cancelled || !token) return;
        const res = await fetch(
          apiUrl(
            `/api/aicheck/projects/${encodeURIComponent(String(projectId))}`,
          ),
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled || !res.ok) return;
        const data = (await res.json()) as { runs?: RunRecord[] };
        const runs = data.runs ?? [];

        const durationsSec = runs
          .filter(
            (r) =>
              r.status === "completed" &&
              r.trigger !== "freeform" &&
              !!r.completedAt &&
              (!language || r.language === language),
          )
          .map(
            (r) =>
              (new Date(r.completedAt as string).getTime() -
                new Date(r.startedAt).getTime()) /
              1000,
          )
          .filter((sec) => Number.isFinite(sec) && sec > 0)
          .sort((a, b) => a - b);

        if (cancelled || durationsSec.length === 0) return;

        const mid = Math.floor(durationsSec.length / 2);
        const medianSec =
          durationsSec.length % 2 === 0
            ? (durationsSec[mid - 1] + durationsSec[mid]) / 2
            : durationsSec[mid];

        setState({
          medianMinutes: Math.max(1, Math.round(medianSec / 60)),
          medianSeconds: medianSec,
        });
      } catch {
        // 付加表示。取得失敗は静的フォールバックに委ねる。
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, language]);

  return state;
}

const STATIC_ESTIMATE_UPPER_BOUND_SEC = 5 * 60;

/**
 * U2: 実行中の進捗ヘッダーに所要時間の目安を表示する。
 * - 過去実績の中央値があれば「目安 約N分」、無ければ静的フォールバック「通常 2〜5 分程度かかります」。
 * - 目安を超過したら不安を煽らないよう「まもなく完了します」に切り替える（止まっているように見せない）。
 */
function EstimatedDurationHint({
  projectId,
  language,
  startedAt,
}: {
  projectId?: number;
  language?: OutputLanguage;
  startedAt: number;
}) {
  const { medianMinutes, medianSeconds } = useEstimatedRunDuration(
    projectId,
    language,
  );
  const [elapsedSec, setElapsedSec] = useState(
    () => (Date.now() - startedAt) / 1000,
  );

  useEffect(() => {
    setElapsedSec((Date.now() - startedAt) / 1000);
    const interval = setInterval(() => {
      setElapsedSec((Date.now() - startedAt) / 1000);
    }, 5000);
    return () => clearInterval(interval);
  }, [startedAt]);

  const thresholdSec = medianSeconds ?? STATIC_ESTIMATE_UPPER_BOUND_SEC;
  const exceeded = elapsedSec > thresholdSec;

  let label: string;
  if (exceeded) {
    label = "まもなく完了します";
  } else if (medianMinutes) {
    label = `目安 約${medianMinutes}分`;
  } else {
    label = "通常 2〜5 分程度かかります";
  }

  return <span className="text-gray-400 text-[11px]">{label}</span>;
}
